import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Cog6ToothIcon,
  MapIcon,
  CpuChipIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  getConfiguration,
  updateConfiguration,
  getCustomFields,
  createCustomField,
  updateCustomField,
  deleteCustomField,
  reorderCustomFields
} from '../services/api';
import { TILE_PROVIDERS } from '../components/VoterMapComponent';
import ModelSwitcher from '../components/ModelSwitcher';

// Field type options for custom fields
const FIELD_TYPES = [
  { value: 'text', label: 'Text (single line)' },
  { value: 'textarea', label: 'Text Area (multi-line)' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'rating', label: 'Rating (1-5 stars)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' }
];

export default function Settings() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('llm');
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Local state for form values
  const [formValues, setFormValues] = useState({
    tile_provider: 'osm_online',
    default_walking_speed_mph: 3.0,
    default_time_per_door_minutes: 3.0
  });

  // Custom fields state
  const [editingField, setEditingField] = useState(null);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [fieldForm, setFieldForm] = useState({
    label: '',
    field_type: 'text',
    description: '',
    required: false,
    placeholder: '',
    options: [],
    min_value: null,
    max_value: null
  });
  const [newOption, setNewOption] = useState({ value: '', label: '' });

  // Fetch custom fields
  const { data: customFieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ['customFields'],
    queryFn: async () => {
      const response = await getCustomFields();
      return response.fields || [];
    }
  });

  // Ensure customFields is always an array
  const customFields = Array.isArray(customFieldsData) ? customFieldsData : [];

  // Create custom field mutation
  const createFieldMutation = useMutation({
    mutationFn: createCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries(['customFields']);
      resetFieldForm();
    }
  });

  // Update custom field mutation
  const updateFieldMutation = useMutation({
    mutationFn: ({ fieldId, updates }) => updateCustomField(fieldId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['customFields']);
      resetFieldForm();
    }
  });

  // Delete custom field mutation
  const deleteFieldMutation = useMutation({
    mutationFn: deleteCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries(['customFields']);
    }
  });

  // Reset field form
  const resetFieldForm = () => {
    setFieldForm({
      label: '',
      field_type: 'text',
      description: '',
      required: false,
      placeholder: '',
      options: [],
      min_value: null,
      max_value: null
    });
    setEditingField(null);
    setShowFieldForm(false);
    setNewOption({ value: '', label: '' });
  };

  // Start editing a field
  const startEditField = (field) => {
    setFieldForm({
      label: field.label || '',
      field_type: field.field_type || 'text',
      description: field.description || '',
      required: field.required || false,
      placeholder: field.placeholder || '',
      options: field.options || [],
      min_value: field.min_value ?? null,
      max_value: field.max_value ?? null
    });
    setEditingField(field.id);
    setShowFieldForm(true);
  };

  // Handle field form submission
  const handleFieldSubmit = (e) => {
    e.preventDefault();
    if (!fieldForm.label.trim()) return;

    const fieldData = {
      ...fieldForm,
      label: fieldForm.label.trim()
    };

    // Clean up options for non-select/radio types
    if (!['select', 'radio'].includes(fieldData.field_type)) {
      delete fieldData.options;
    }

    // Clean up min/max for non-number/rating types
    if (!['number', 'rating'].includes(fieldData.field_type)) {
      delete fieldData.min_value;
      delete fieldData.max_value;
    }

    if (editingField) {
      updateFieldMutation.mutate({ fieldId: editingField, updates: fieldData });
    } else {
      createFieldMutation.mutate(fieldData);
    }
  };

  // Add option to select/radio field
  const addOption = () => {
    if (!newOption.value.trim() || !newOption.label.trim()) return;
    setFieldForm(prev => ({
      ...prev,
      options: [...prev.options, { ...newOption }]
    }));
    setNewOption({ value: '', label: '' });
  };

  // Remove option from select/radio field
  const removeOption = (index) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Fetch current configuration
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['configuration'],
    queryFn: getConfiguration,
    onSuccess: (data) => {
      if (data) {
        setFormValues(prev => ({ ...prev, ...data }));
      }
    }
  });

  // Update configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: ({ section, updates }) => updateConfiguration(section, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['configuration']);
      setUnsavedChanges(false);
    }
  });

  // Update local form state
  const handleInputChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setUnsavedChanges(true);
  };

  // Save map settings
  const handleSaveMapSettings = () => {
    updateConfigMutation.mutate({
      section: 'election',
      updates: {
        tile_provider: formValues.tile_provider
      }
    });
  };

  // Save route settings
  const handleSaveRouteSettings = () => {
    updateConfigMutation.mutate({
      section: 'election',
      updates: {
        default_walking_speed_mph: parseFloat(formValues.default_walking_speed_mph),
        default_time_per_door_minutes: parseFloat(formValues.default_time_per_door_minutes)
      }
    });
  };

  // Update form when config loads
  useEffect(() => {
    if (config) {
      setFormValues(prev => ({ ...prev, ...config }));
    }
  }, [config]);

  const tabs = [
    { id: 'llm', name: 'LLM Configuration', icon: CpuChipIcon },
    { id: 'map', name: 'Map Settings', icon: MapIcon },
    { id: 'routes', name: 'Route Optimization', icon: TruckIcon },
    { id: 'fields', name: 'Custom Fields', icon: ClipboardDocumentListIcon }
  ];

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Cog6ToothIcon className="h-8 w-8 mr-3 text-blue-600" />
          Settings
        </h1>
        <p className="mt-2 text-gray-600">
          Configure LLM providers, map settings, and route optimization defaults.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* LLM Configuration Tab */}
      {activeTab === 'llm' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">LLM Provider Configuration</h2>
            <p className="text-sm text-gray-600 mb-6">
              Select and configure the AI model used for natural language visualization and schema analysis.
              Changes take effect immediately without requiring a restart.
            </p>
            <ModelSwitcher showAsCard={false} />
          </div>
        </div>
      )}

      {/* Map Settings Tab */}
      {activeTab === 'map' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Map Configuration</h2>
          <p className="text-sm text-gray-600 mb-6">
            Configure map tile providers and visualization defaults.
          </p>

          {/* Tile Provider Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Map Tile Provider
            </label>
            <select
              value={formValues.tile_provider}
              onChange={(e) => handleInputChange('tile_provider', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(TILE_PROVIDERS).map(([key, provider]) => (
                <option key={key} value={key}>
                  {provider.name}
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Use "Local TileServer" for offline operation after running the OSM setup script.
            </p>
          </div>

          {/* Tile Provider Preview */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Tile URL:</span>{' '}
              <code className="text-xs bg-gray-200 px-2 py-1 rounded">
                {TILE_PROVIDERS[formValues.tile_provider]?.url || 'Unknown'}
              </code>
            </div>
          </div>

          <button
            onClick={handleSaveMapSettings}
            disabled={updateConfigMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {updateConfigMutation.isPending ? (
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Save Map Settings
          </button>
        </div>
      )}

      {/* Route Optimization Tab */}
      {activeTab === 'routes' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Route Optimization Defaults</h2>
          <p className="text-sm text-gray-600 mb-6">
            Set default values for canvasser route optimization. These can be overridden when generating routes.
          </p>

          {/* Walking Speed */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Average Walking Speed (mph)
            </label>
            <input
              type="number"
              step="0.1"
              min="1"
              max="5"
              value={formValues.default_walking_speed_mph}
              onChange={(e) => handleInputChange('default_walking_speed_mph', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-2 text-sm text-gray-500">
              Typical walking speed is 2.5-3.5 mph. Used to estimate route duration.
            </p>
          </div>

          {/* Time Per Door */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Average Time Per Door (minutes)
            </label>
            <input
              type="number"
              step="0.5"
              min="1"
              max="15"
              value={formValues.default_time_per_door_minutes}
              onChange={(e) => handleInputChange('default_time_per_door_minutes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-2 text-sm text-gray-500">
              Time spent at each door including walking to door, interaction, and notes.
            </p>
          </div>

          {/* Calculated Example */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-blue-800">
              <span className="font-medium">Example Calculation:</span><br />
              With a 4-hour shift at {formValues.default_walking_speed_mph} mph and {formValues.default_time_per_door_minutes} min/door,
              a canvasser can cover approximately{' '}
              <span className="font-bold">
                {Math.floor(240 / (parseFloat(formValues.default_time_per_door_minutes) + 2))} doors
              </span>
              {' '}(assuming average 0.1 mile between doors).
            </div>
          </div>

          <button
            onClick={handleSaveRouteSettings}
            disabled={updateConfigMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {updateConfigMutation.isPending ? (
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Save Route Settings
          </button>
        </div>
      )}

      {/* Custom Fields Tab */}
      {activeTab === 'fields' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Custom Fields</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Define custom fields that canvassers will fill out when checking in voters.
                </p>
              </div>
              <button
                onClick={() => {
                  resetFieldForm();
                  setShowFieldForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Field
              </button>
            </div>

            {/* Field Form */}
            {showFieldForm && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">
                    {editingField ? 'Edit Field' : 'New Field'}
                  </h3>
                  <button
                    onClick={resetFieldForm}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleFieldSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Label *
                      </label>
                      <input
                        type="text"
                        value={fieldForm.label}
                        onChange={(e) => setFieldForm(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="e.g., Support Level"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Type
                      </label>
                      <select
                        value={fieldForm.field_type}
                        onChange={(e) => setFieldForm(prev => ({ ...prev, field_type: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        {FIELD_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={fieldForm.description}
                      onChange={(e) => setFieldForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Help text shown to canvassers"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Options for select/radio */}
                  {['select', 'radio'].includes(fieldForm.field_type) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Options
                      </label>
                      <div className="space-y-2">
                        {fieldForm.options.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded flex-1">
                              {opt.label} ({opt.value})
                            </span>
                            <button
                              type="button"
                              onClick={() => removeOption(idx)}
                              className="p-1 text-red-500 hover:text-red-700"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newOption.value}
                            onChange={(e) => setNewOption(prev => ({ ...prev, value: e.target.value }))}
                            placeholder="Value"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <input
                            type="text"
                            value={newOption.label}
                            onChange={(e) => setNewOption(prev => ({ ...prev, label: e.target.value }))}
                            placeholder="Label"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <button
                            type="button"
                            onClick={addOption}
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Min/Max for number/rating */}
                  {['number', 'rating'].includes(fieldForm.field_type) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={fieldForm.min_value ?? ''}
                          onChange={(e) => setFieldForm(prev => ({
                            ...prev,
                            min_value: e.target.value ? Number(e.target.value) : null
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={fieldForm.max_value ?? ''}
                          onChange={(e) => setFieldForm(prev => ({
                            ...prev,
                            max_value: e.target.value ? Number(e.target.value) : null
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="required"
                      checked={fieldForm.required}
                      onChange={(e) => setFieldForm(prev => ({ ...prev, required: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <label htmlFor="required" className="ml-2 text-sm text-gray-700">
                      Required field
                    </label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={resetFieldForm}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createFieldMutation.isPending || updateFieldMutation.isPending}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                    >
                      {(createFieldMutation.isPending || updateFieldMutation.isPending) ? (
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckIcon className="h-4 w-4 mr-2" />
                      )}
                      {editingField ? 'Update Field' : 'Create Field'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Field List */}
            {fieldsLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : customFields.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClipboardDocumentListIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No custom fields defined yet.</p>
                <p className="text-sm">Click "Add Field" to create your first custom field.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {customFields.filter(f => f.active !== false).map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="cursor-move text-gray-400">
                      <Bars3Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{field.label}</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
                        </span>
                        {field.required && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            Required
                          </span>
                        )}
                      </div>
                      {field.description && (
                        <p className="text-sm text-gray-500 mt-1">{field.description}</p>
                      )}
                      {field.options?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {field.options.map((opt, i) => (
                            <span key={i} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              {opt.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditField(field)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Edit field"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete field "${field.label}"?`)) {
                            deleteFieldMutation.mutate(field.id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete field"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Field Preview */}
          {customFields.filter(f => f.active !== false).length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              <p className="text-sm text-gray-600 mb-4">
                This is how the fields will appear to canvassers on mobile:
              </p>
              <div className="max-w-sm mx-auto p-4 border-2 border-gray-300 rounded-xl bg-gray-50">
                <div className="space-y-4">
                  {customFields.filter(f => f.active !== false).map((field) => (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.field_type === 'text' && (
                        <input
                          type="text"
                          placeholder={field.placeholder || ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          disabled
                        />
                      )}
                      {field.field_type === 'textarea' && (
                        <textarea
                          placeholder={field.placeholder || ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          rows={2}
                          disabled
                        />
                      )}
                      {field.field_type === 'select' && (
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" disabled>
                          <option>Select...</option>
                          {field.options?.map((opt, i) => (
                            <option key={i}>{opt.label}</option>
                          ))}
                        </select>
                      )}
                      {field.field_type === 'checkbox' && (
                        <div className="flex items-center">
                          <input type="checkbox" className="h-4 w-4 rounded border-gray-300" disabled />
                          <span className="ml-2 text-sm text-gray-600">Yes</span>
                        </div>
                      )}
                      {field.field_type === 'radio' && (
                        <div className="space-y-1">
                          {field.options?.map((opt, i) => (
                            <div key={i} className="flex items-center">
                              <input type="radio" className="h-4 w-4 border-gray-300" disabled />
                              <span className="ml-2 text-sm text-gray-600">{opt.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {field.field_type === 'rating' && (
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <span key={n} className="text-2xl text-gray-300">★</span>
                          ))}
                        </div>
                      )}
                      {field.field_type === 'number' && (
                        <input
                          type="number"
                          placeholder={field.placeholder || ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          disabled
                        />
                      )}
                      {field.field_type === 'date' && (
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          disabled
                        />
                      )}
                      {field.description && (
                        <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {unsavedChanges && (
        <div className="fixed bottom-4 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center text-yellow-800">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            You have unsaved changes
          </div>
        </div>
      )}
    </div>
  );
}
